import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/api';
import GradientHeading from './GradientHeading';

const formatTimestamp = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts * 1000);
  // Format: YYYY-MM-DD HH:mm
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hour = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hour}:${min}`;
};

const PromotionDetail = () => {
  const { activityId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiClient.get(`/promotions/${activityId}`);
        setDetail(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch promotion details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [activityId]);

  if (loading) return <div style={{ padding: 48, fontSize: 20, color: '#25f4ee', fontWeight: 700 }}>Loading promotion details...</div>;
  if (error) return <div style={{ padding: 48, color: '#fe2c55', background: '#fff0f3', border: '1px solid #fe2c55', borderRadius: 10, fontWeight: 600 }}>{error}</div>;
  if (!detail) return null;
  const d = detail;

  return (
    <div style={{ padding: 0, minHeight: '100vh', background: 'linear-gradient(120deg, #f8fafc 0%, #e0f7fa 100%)', borderRadius: 18 }}>
      <div style={{
        maxWidth: '100%',
        // margin: '0 auto',
        background: 'rgba(255,255,255,0.97)',
        borderRadius: 18,
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.10)',
        padding: '36px 28px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <button onClick={() => navigate(-1)} style={{ marginBottom: 18, background: 'none', border: 'none', color: '#000000', fontWeight: 700, fontSize: 17, cursor: 'pointer', letterSpacing: '0.01em' }}>&larr; Back</button>
        <GradientHeading style={{ fontSize: 30, marginBottom: 10 }}>{d.title}</GradientHeading>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 24,
          marginBottom: 28,
        }}>
          <div style={{
            flex: '1 1 360px',
            background: '#f8fafc',
            borderRadius: 12,
            boxShadow: '0 1px 4px #e0e7ef',
            padding: 18,
            minWidth: 220,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontWeight: 700, color: '#64748b', minWidth: 110 }}>Status</div>
                <div>
                  <span style={{
                    padding: '4px 14px',
                    borderRadius: 8,
                    background: d.status === 'ONGOING' ? '#e0f7fa' : d.status === 'SCHEDULED' ? '#fffbe6' : '#fbeaea',
                    color: d.status === 'ONGOING' ? '#0097a7' : d.status === 'SCHEDULED' ? '#bfa100' : '#c62828',
                    fontWeight: 700,
                    fontSize: 15
                  }}>{d.status}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontWeight: 700, color: '#64748b', minWidth: 110 }}>Type</div>
                <div style={{ fontWeight: 600 }}>{d.activity_type}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontWeight: 700, color: '#64748b', minWidth: 110 }}>Start</div>
                <div>{formatTimestamp(d.begin_time)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontWeight: 700, color: '#64748b', minWidth: 110 }}>End</div>
                <div>{formatTimestamp(d.end_time)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontWeight: 700, color: '#64748b', minWidth: 110 }}>Created By</div>
                <div>{d.created_by || '-'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontWeight: 700, color: '#64748b', minWidth: 110 }}>Created At</div>
                <div>{formatTimestamp(d.created_at)}</div>
              </div>
            </div>
          </div>
          <div style={{
            flex: '2 1 340px',
            background: '#f8fafc',
            borderRadius: 12,
            boxShadow: '0 1px 4px #e0e7ef',
            padding: 18,
            minWidth: 260,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            justifyContent: 'center',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontWeight: 700, color: '#64748b', minWidth: 120 }}>Discount Type</div>
                <div>{d.discount ? Object.keys(d.discount).join(', ') : '-'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontWeight: 700, color: '#64748b', minWidth: 120 }}>Target Areas</div>
                <div>{d.discount?.shipping_discount?.area_scope?.specific_areas || '-'}</div>
              </div>
              {d.description && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ fontWeight: 700, color: '#64748b', minWidth: 120, marginTop: 2 }}>Description</div>
                  <div style={{ color: '#222', background: '#f3f6fa', borderRadius: 8, padding: 12, fontStyle: 'italic', boxShadow: '0 1px 4px #e0e7ef', marginTop: 0 }}>
                    {d.description}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 22, margin: '0 0 18px 0', fontWeight: 700, color: '#000000', letterSpacing: '-0.5px' }}>Products in Promotion</h2>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px #e0e7ef', marginBottom: 0 }}>
            <thead>
              <tr style={{ background: 'linear-gradient(90deg, #25f4ee 0%, #e0f7fa 100%)', fontWeight: 700, color: '#222' }}>
                <th style={{ padding: 12, borderBottom: '2px solid #e0e7ef', fontSize: 15 }}>Product ID</th>
                <th style={{ padding: 12, borderBottom: '2px solid #e0e7ef', fontSize: 15 }}>Name</th>
                <th style={{ padding: 12, borderBottom: '2px solid #e0e7ef', fontSize: 15 }}>Price</th>
              </tr>
            </thead>
            <tbody>
              {(d.products || []).map((prod) => (
                <tr key={prod.id} style={{ borderBottom: '1px solid #f1f1f1', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0faff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  <td style={{ padding: 10, fontWeight: 600 }}>{prod.id}</td>
                  <td style={{ padding: 10 }}>{prod.name || '-'}</td>
                  <td style={{ padding: 10 }}>{prod.price ? `$${prod.price}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Shipping discount info */}
        {d.discount?.shipping_discount && (
          <div style={{ marginTop: 0, background: '#e0f7fa', padding: 18, borderRadius: 10, fontWeight: 600, color: '#0097a7', fontSize: 16, boxShadow: '0 1px 4px #e0e7ef', marginBottom: 0 }}>
            <span role="img" aria-label="shipping" style={{ marginRight: 8 }}>🚚</span>
            <strong>Shipping Discount:</strong> Free shipping to {d.discount.shipping_discount.area_scope?.specific_areas || '-'}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionDetail;
